import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Switch,
  FormControlLabel,
  Chip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Alert,
  Snackbar,
  InputAdornment,
  Select,
  FormControl,
  InputLabel,
  Avatar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  LinearProgress,
  Paper,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  MoreVert as MoreVertIcon,
  Search as SearchIcon,
  AttachMoney as PriceIcon,
  Speed as QuickIcon,
  Image as ImageIcon,
  LocalOffer as OfferIcon,
  Schedule as ScheduleIcon,
  ExpandMore as ExpandMoreIcon,
  Scale as WeightIcon,
  FolderOpen as EmptyIcon
} from '@mui/icons-material';
import { useForm, Controller, useFieldArray } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { Product, ProductFormData, WeightPrice } from '../../types/models';
import { productService, categoryService } from '../../services/firebase';
import ImageUpload from '../../components/ImageUpload/ImageUpload';
import QuickPriceUpdate from '../../components/QuickPriceUpdate/QuickPriceUpdate';
import { useAuditLog } from '../../hooks/useAuditLog';
import { AuditAction } from '../../types/audit';
import { logger } from '../../services/loggerService';

// Validation schema - matching ProductFormData interface
const productSchema: yup.ObjectSchema<any> = yup.object({
  name: yup.string().required('Product name is required').min(2, 'Name must be at least 2 characters'),
  description: yup.string().required('Description is required').min(10, 'Description must be at least 10 characters'),
  category: yup.string().required('Category is required'),
  pricePerKg: yup.number()
    .positive('Selling price must be greater than 0')
    .required('Selling price is required')
    .test('price-less-than-mrp', 'Selling price cannot be greater than MRP', function(value) {
      const { originalPrice } = this.parent;
      if (!originalPrice || !value) return true; // Skip validation if MRP not set
      return value <= originalPrice;
    }),
  originalPrice: yup.number().positive('MRP must be greater than 0').optional(),
  unit: yup.string().required('Unit is required'),
  images: yup.array().min(1, 'At least one product image is required'),
  isAvailable: yup.boolean().required(),
  isOrganic: yup.boolean().required(),
  isSeasonal: yup.boolean().required(),
  preOrderOnly: yup.boolean().required(),
});

const ProductsWeightBased: React.FC = () => {
  const [products, setProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [productToDelete, setProductToDelete] = useState<Product | null>(null);
  const [quickPriceOpen, setQuickPriceOpen] = useState(false);
  
  const { logProductAction } = useAuditLog();

  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors, isSubmitting }
  } = useForm<ProductFormData>({
    resolver: yupResolver(productSchema),
    defaultValues: {
      name: '',
      description: '',
      category: '',
      price: 0,
      originalPrice: 0,
      unit: 'kg',
      stockQuantity: 0,
      pricePerKg: 0,
      weightPrices: [],
      images: [],
      isAvailable: true,
      isOrganic: false,
      preOrderOnly: false,
      nutritionInfo: {},
      minOrderQuantity: 1,
      healthBenefits: '',
      storageTemperature: '',
      shelfLife: '',
      disclaimer: '',
      customerCareEmail: '',
      countryOfOrigin: 'India',
      returnPolicy: '',
      storageTips: ''
    }
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'weightPrices'
  });

  const pricePerKg = watch('pricePerKg');
  const unit = watch('unit');
  const originalPricePerKg = watch('originalPrice');

  // Predefined weight templates for each unit
  const weightTemplates: Record<string, string[]> = {
    'kg': ['250g', '500g', '1kg', '2kg', '5kg', '10kg'],
    'litre': ['250ml', '500ml', '1L', '2L', '5L', '10L'],
    'g': ['100g', '250g', '500g', '1kg'],
    'ml': ['100ml', '250ml', '500ml', '1L'],
    'piece': ['1piece', '5pieces', '10pieces', '25pieces', '50pieces', '100pieces']
  };

  // Auto-populate weight options when unit changes
  useEffect(() => {
    const currentWeightPrices = watch('weightPrices');
    const templates = weightTemplates[unit] || [];
    
    // Only auto-populate if weight options are empty
    if ((!currentWeightPrices || currentWeightPrices.length === 0) && templates.length > 0) {
      const initialWeightPrices = templates.map(weight => ({
        weight,
        price: 0,
        originalPrice: 0,
        stockQuantity: 0
      }));
      setValue('weightPrices', initialWeightPrices);
    }
  }, [unit, setValue]);

  // Production-ready weight parsing function
  const parseWeightToMultiplier = (weightStr: string, baseUnit: string): number => {
    if (!weightStr || !weightStr.trim()) return 1;

    const normalized = weightStr.toLowerCase().trim();
    
    // Extract numeric value using precise regex
    const numericMatch = normalized.match(/^(\d+(?:\.\d+)?)/);
    if (!numericMatch) {
      logger.warn('Could not extract numeric value from weight', { weightStr });
      return 1;
    }
    
    const numericValue = parseFloat(numericMatch[1]);
    if (isNaN(numericValue) || numericValue <= 0) {
      logger.warn('Invalid numeric value in weight', { weightStr, numericValue });
      return 1;
    }

    // Extract unit by removing numeric value and whitespace
    const unitPart = normalized.replace(/^(\d+(?:\.\d+)?)\s*/, '').trim();
    
    // Unit conversion table based on base unit
    const conversionTable: Record<string, Record<string, number>> = {
      'kg': {
        'kg': 1,
        'g': 0.001,
        'gram': 0.001,
        'grams': 0.001,
        'kilogram': 1,
        'kilograms': 1,
      },
      'litre': {
        'l': 1,
        'litre': 1,
        'litres': 1,
        'liter': 1,
        'liters': 1,
        'ml': 0.001,
        'millilitre': 0.001,
        'millilitres': 0.001,
        'milliliter': 0.001,
        'milliliters': 0.001,
      },
      'piece': {
        'piece': 1,
        'pieces': 1,
        'pc': 1,
        'pcs': 1,
        'unit': 1,
        'units': 1,
      },
      'g': {
        'g': 1,
        'gram': 1,
        'grams': 1,
        'kg': 1000,
        'kilogram': 1000,
        'kilograms': 1000,
      },
      'ml': {
        'ml': 1,
        'millilitre': 1,
        'millilitres': 1,
        'milliliter': 1,
        'milliliters': 1,
        'l': 1000,
        'litre': 1000,
        'litres': 1000,
        'liter': 1000,
        'liters': 1000,
      }
    };

    // Get conversion factors for current base unit
    const conversions = conversionTable[baseUnit.toLowerCase()] || {};
    const conversionFactor = conversions[unitPart] || 1;
    
    // Calculate final multiplier
    const multiplier = numericValue * conversionFactor;
    
    logger.debug('Weight parsing', { 
      weightStr, 
      baseUnit, 
      numericValue, 
      unitPart, 
      conversionFactor, 
      multiplier 
    });
    
    return multiplier;
  };

  // Calculate prices for all weight options based on base price
  const handleCalculatePrices = () => {
    const currentWeightPrices = watch('weightPrices');
    if (!pricePerKg || pricePerKg <= 0) {
      setSnackbar({ open: true, message: 'Please set base selling price first', severity: 'error' });
      return;
    }

    if (!currentWeightPrices || currentWeightPrices.length === 0) {
      setSnackbar({ open: true, message: 'Please add weight options first', severity: 'error' });
      return;
    }

    const mrpPerKg = originalPricePerKg || pricePerKg;
    const updatedWeightPrices = currentWeightPrices.map((wp: any) => {
      const multiplier = parseWeightToMultiplier(wp.weight || '', unit);
      
      return {
        ...wp,
        price: Math.round(pricePerKg * multiplier),
        originalPrice: Math.round(mrpPerKg * multiplier)
      };
    });

    setValue('weightPrices', updatedWeightPrices);
    setSnackbar({ open: true, message: 'Prices calculated successfully', severity: 'success' });
  };

  const loadProducts = async () => {
    setLoading(true);
    try {
      const [productsData, categoriesData] = await Promise.all([
        productService.getProducts(),
        categoryService.getCategories()
      ]);
      setProducts(productsData);
      setCategories(categoriesData);
    } catch (error) {
      logger.error('Error loading products', error, { component: 'ProductsWeightBased', action: 'loadProducts' });
      setSnackbar({ open: true, message: 'Failed to load products', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadProducts();
  }, []);

  const handleOpenDialog = (product?: Product) => {
    if (product) {
      setEditingProduct(product);
      
      const weightPricesWithOriginalPrice = product.weightPrices?.map(wp => ({
        weight: wp.weight,
        price: wp.price,
        originalPrice: wp.originalPrice || wp.price,
        stockQuantity: wp.stockQuantity
      })) || [
        { weight: '250g', price: 0, originalPrice: 0, stockQuantity: 0 },
        { weight: '500g', price: 0, originalPrice: 0, stockQuantity: 0 },
        { weight: '1kg', price: 0, originalPrice: 0, stockQuantity: 0 }
      ];
      
      reset({
        name: product.name,
        description: product.description,
        category: product.category,
        price: product.price,
        originalPrice: product.originalPrice || 0,
        unit: product.unit,
        stockQuantity: product.stockQuantity,
        pricePerKg: product.pricePerKg || 0,
        weightPrices: weightPricesWithOriginalPrice,
        images: product.images,
        primaryImage: product.primaryImage,
        isAvailable: product.isAvailable,
        isOrganic: product.isOrganic,
        isSeasonal: product.isSeasonal || false,
        preOrderOnly: product.preOrderOnly,
        estimatedDelivery: product.estimatedDelivery,
        minOrderQuantity: product.minOrderQuantity || 1,
        nutritionInfo: product.nutritionInfo || {},
        healthBenefits: product.healthBenefits || '',
        storageTemperature: product.storageTemperature || '',
        shelfLife: product.shelfLife || '',
        disclaimer: product.disclaimer || '',
        customerCareEmail: product.customerCareEmail || '',
        countryOfOrigin: product.countryOfOrigin || 'India',
        returnPolicy: product.returnPolicy || '',
        storageTips: product.storageTips || ''
      });
    } else {
      setEditingProduct(null);
      reset({
        name: '',
        description: '',
        category: '',
        price: 0,
        originalPrice: 0,
        unit: 'kg',
        stockQuantity: 0,
        pricePerKg: 0,
        weightPrices: [
          { weight: '250g', price: 0, originalPrice: 0, stockQuantity: 0 },
          { weight: '500g', price: 0, originalPrice: 0, stockQuantity: 0 },
          { weight: '1kg', price: 0, originalPrice: 0, stockQuantity: 0 }
        ],
        images: [],
        isAvailable: true,
        isOrganic: false,
        isSeasonal: false,
        preOrderOnly: false,
        nutritionInfo: {},
        minOrderQuantity: 1
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingProduct(null);
    reset();
  };

  const onSubmit = async (data: ProductFormData) => {
    try {
      // Find category details to populate categoryId and categoryName
      const selectedCategory = categories.find(cat => cat.name === data.category);
      
      const productData = {
        ...data,
        categoryId: selectedCategory?.id || data.category.toLowerCase().replace(/\s+/g, '-'),
        categoryName: data.category,
        createdAt: editingProduct?.createdAt || new Date(),
        updatedAt: new Date(),
        rating: editingProduct?.rating || 0,
        reviewCount: editingProduct?.reviewCount || 0,
        primaryImage: data.images && data.images.length > 0 ? data.images[0] : undefined
      };

      if (editingProduct) {
        await productService.updateProduct(editingProduct.id, productData);
        
        // Log update with changes
        await logProductAction(
          AuditAction.PRODUCT_UPDATE,
          editingProduct.id,
          data.name,
          { before: editingProduct, after: productData },
          true
        );
        
        setSnackbar({ open: true, message: 'Product updated successfully', severity: 'success' });
      } else {
        const newProductId = await productService.addProduct(productData);
        
        // Log creation
        await logProductAction(
          AuditAction.PRODUCT_CREATE,
          newProductId,
          data.name,
          undefined,
          true
        );
        
        setSnackbar({ open: true, message: 'Product created successfully', severity: 'success' });
      }

      await loadProducts();
      handleCloseDialog();
    } catch (error: any) {
      logger.error('Error saving product', error, { component: 'ProductsWeightBased', action: 'onSubmit' });
      
      // Log failed action
      await logProductAction(
        editingProduct ? AuditAction.PRODUCT_UPDATE : AuditAction.PRODUCT_CREATE,
        editingProduct?.id || 'unknown',
        data.name,
        undefined,
        false,
        error?.message
      );
      
      setSnackbar({ open: true, message: `Error saving product: ${error?.message || 'Unknown error'}`, severity: 'error' });
    }
  };

  const handleQuickPriceUpdate = (product: Product) => {
    setSelectedProduct(product);
    setQuickPriceOpen(true);
    setAnchorEl(null);
  };

  const handlePriceUpdate = async (productId: string, newPrice: number, originalPrice: number, weightPrices?: Array<{weight: string, price: number, originalPrice: number, stockQuantity?: number}>) => {
    try {
      const updatedProduct = products.find(p => p.id === productId);
      if (!updatedProduct) return;

      // Use provided weight prices if available (from manual editing), otherwise calculate
      let updatedWeightPrices = updatedProduct.weightPrices;
      
      if (weightPrices && weightPrices.length > 0) {
        // User manually edited weight prices - use them directly
        updatedWeightPrices = weightPrices.map((wp, index) => ({
          weight: wp.weight,
          price: wp.price,
          originalPrice: wp.originalPrice,
          stockQuantity: wp.stockQuantity || updatedProduct.weightPrices?.[index]?.stockQuantity || 0
        }));
      } else if (updatedProduct.weightPrices && updatedProduct.weightPrices.length > 0) {
        // No manual edits - calculate from base price
        const mrpPerKg = originalPrice || newPrice;
        updatedWeightPrices = updatedProduct.weightPrices.map(wp => {
          const multiplier = parseWeightToMultiplier(wp.weight, updatedProduct.unit || 'kg');
          return {
            ...wp,
            price: Math.round(newPrice * multiplier),
            originalPrice: Math.round(mrpPerKg * multiplier)
          };
        });
      }

      const updateData: any = {
        price: newPrice,
        pricePerKg: newPrice,
        originalPrice: originalPrice || newPrice,
        updatedAt: new Date()
      };

      // Add updated weight prices if they exist
      if (updatedWeightPrices && updatedWeightPrices.length > 0) {
        updateData.weightPrices = updatedWeightPrices;
      }

      await productService.updateProduct(productId, updateData);

      setProducts(prev => prev.map(p => 
        p.id === productId 
          ? { 
              ...p, 
              price: newPrice, 
              pricePerKg: newPrice, 
              originalPrice: originalPrice || p.originalPrice,
              weightPrices: updatedWeightPrices,
              updatedAt: new Date() 
            }
          : p
      ));

      await logProductAction(
        AuditAction.PRODUCT_UPDATE,
        productId,
        updatedProduct.name,
        { before: { pricePerKg: updatedProduct.pricePerKg }, after: { pricePerKg: newPrice } },
        true
      );

      setSnackbar({ open: true, message: 'All prices updated successfully', severity: 'success' });
    } catch (error) {
      setSnackbar({ open: true, message: 'Failed to update price', severity: 'error' });
    }
  };

  const handleDeleteClick = (product: Product) => {
    setProductToDelete(product);
    setDeleteDialogOpen(true);
    setAnchorEl(null);
  };

  const handleDeleteConfirm = async () => {
    if (!productToDelete) return;
    
    try {
      await productService.deleteProduct(productToDelete.id);
      
      // Log deletion
      await logProductAction(
        AuditAction.PRODUCT_DELETE,
        productToDelete.id,
        productToDelete.name,
        undefined,
        true
      );
      
      await loadProducts();
      setSnackbar({ open: true, message: 'Product deleted successfully', severity: 'success' });
    } catch (error: any) {
      logger.error('Error deleting product', error, { component: 'ProductsWeightBased', action: 'handleDeleteConfirm' });
      
      await logProductAction(
        AuditAction.PRODUCT_DELETE,
        productToDelete.id,
        productToDelete.name,
        undefined,
        false,
        error?.message
      );
      
      setSnackbar({ open: true, message: `Error deleting product: ${error?.message || 'Unknown error'}`, severity: 'error' });
    } finally {
      setDeleteDialogOpen(false);
      setProductToDelete(null);
    }
  };

  const handleToggleAvailability = async (product: Product) => {
    try {
      const newAvailability = !product.isAvailable;
      await productService.updateProduct(product.id, { 
        isAvailable: newAvailability,
        updatedAt: new Date()
      });
      
      // Log availability toggle
      await logProductAction(
        AuditAction.PRODUCT_TOGGLE_AVAILABILITY,
        product.id,
        product.name,
        { 
          before: { isAvailable: product.isAvailable }, 
          after: { isAvailable: newAvailability } 
        },
        true
      );
      
      await loadProducts();
      setSnackbar({ 
        open: true, 
        message: `Product ${newAvailability ? 'enabled' : 'disabled'} successfully`, 
        severity: 'success' 
      });
    } catch (error: any) {
      logger.error('Error toggling product availability', error, { component: 'ProductsWeightBased', action: 'handleToggleAvailability' });
      setSnackbar({ open: true, message: `Error updating product: ${error?.message || 'Unknown error'}`, severity: 'error' });
    }
    setAnchorEl(null);
  };

  const formatCurrency = (amount: number) => `₹${amount.toLocaleString()}`;

  const getWeightPriceDisplay = (product: Product) => {
    if (product.weightPrices && product.weightPrices.length > 0) {
      return product.weightPrices.map(wp => `${wp.weight}: ${formatCurrency(wp.price)}`).join(' | ');
    }
    return `${formatCurrency(product.price)} per ${product.unit}`;
  };

  // Filter products
  const filteredProducts = products.filter(product => {
    const matchesSearch = product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         product.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = !categoryFilter || product.category === categoryFilter;
    return matchesSearch && matchesCategory;
  });

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, color: 'text.primary' }}>
            Products (Weight & Volume Based)
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mt: 0.5 }}>
            Manage products with flexible pricing for kg (250g, 500g, 1kg) and litre (250ml, 500ml, 1L) options
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
          sx={{
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 600,
            px: 3
          }}
        >
          Add Product
        </Button>
      </Box>

      {/* Filters */}
      <Box sx={{ mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <TextField
          size="small"
          placeholder="Search products..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{ minWidth: 250 }}
        />
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>Category</InputLabel>
          <Select
            value={categoryFilter}
            label="Category"
            onChange={(e) => setCategoryFilter(e.target.value)}
          >
            <MenuItem value="">All Categories</MenuItem>
            {categories.map((category) => (
              <MenuItem key={category.id} value={category.name}>
                {category.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* Loading State */}
      {loading && <LinearProgress sx={{ mb: 3 }} />}

      {/* Empty State */}
      {!loading && products.length === 0 && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            py: 8,
            textAlign: 'center'
          }}
        >
          <EmptyIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
            No products yet
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Get started by adding your first product
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600
            }}
          >
            Add Product
          </Button>
        </Box>
      )}

      {/* Products Table */}
      {!loading && products.length > 0 && (
      <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Product</TableCell>
                <TableCell>Category</TableCell>
                <TableCell>Pricing</TableCell>
                <TableCell>Stock</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredProducts.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <Typography variant="body1" color="text.secondary">
                      No products found. Click "Add Product" to create one.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredProducts
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((product) => (
                  <TableRow key={product.id} hover>
                    <TableCell>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                          {product.name}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                          {product.isOrganic && (
                            <Chip label="Organic" size="small" color="success" />
                          )}
                          {product.preOrderOnly && (
                            <Chip label="Pre-order" size="small" color="warning" />
                          )}
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip label={product.category} size="small" variant="outlined" />
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {getWeightPriceDisplay(product)}
                          </Typography>
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleQuickPriceUpdate(product);
                            }}
                            sx={{ 
                              color: 'primary.main',
                              bgcolor: 'primary.lighter',
                              '&:hover': {
                                bgcolor: 'primary.light',
                                transform: 'scale(1.1)'
                              },
                              transition: 'all 0.2s'
                            }}
                            title="Quick Price Update"
                          >
                            <QuickIcon fontSize="small" />
                          </IconButton>
                        </Box>
                        {product.pricePerKg && (
                          <Typography variant="caption" color="text.secondary">
                            Base: {formatCurrency(product.pricePerKg)}/kg
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      {product.weightPrices ? (
                        <Box>
                          {product.weightPrices.map((wp, index) => (
                            <Typography key={index} variant="caption" sx={{ display: 'block' }}>
                              {wp.weight}: {wp.stockQuantity}
                            </Typography>
                          ))}
                        </Box>
                      ) : (
                        <Typography variant="body2">{product.stockQuantity}</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={product.isAvailable ? 'Available' : 'Unavailable'}
                        size="small"
                        color={product.isAvailable ? 'success' : 'default'}
                        sx={{ fontWeight: 600 }}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        onClick={(e) => {
                          setAnchorEl(e.currentTarget);
                          setSelectedProduct(product);
                        }}
                        size="small"
                      >
                        <MoreVertIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={filteredProducts.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(event, newPage) => setPage(newPage)}
          onRowsPerPageChange={(event) => {
            setRowsPerPage(parseInt(event.target.value, 10));
            setPage(0);
          }}
        />
      </Paper>
      )}

      {/* Context Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
      >
        <MenuItem onClick={() => {
          handleOpenDialog(selectedProduct!);
          setAnchorEl(null);
        }}>
          <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Edit</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleToggleAvailability(selectedProduct!)}>
          <ListItemIcon><QuickIcon fontSize="small" /></ListItemIcon>
          <ListItemText>
            {selectedProduct?.isAvailable ? 'Disable' : 'Enable'}
          </ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleDeleteClick(selectedProduct!)} sx={{ color: 'error.main' }}>
          <ListItemIcon><DeleteIcon fontSize="small" sx={{ color: 'error.main' }} /></ListItemIcon>
          <ListItemText>Delete</ListItemText>
        </MenuItem>
      </Menu>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="lg" fullWidth>
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogTitle>
            {editingProduct ? 'Edit Product' : 'Add New Product'}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 1 }}>
              {/* Basic Information */}
              <Accordion defaultExpanded>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="h6">Basic Information</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                      <Box sx={{ flex: '1 1 300px', minWidth: '300px' }}>
                        <Controller
                          name="name"
                          control={control}
                          rules={{ required: 'Product name is required' }}
                          render={({ field }) => (
                            <TextField
                              {...field}
                              label="Product Name"
                              fullWidth
                              error={!!errors.name}
                              helperText={errors.name?.message}
                            />
                          )}
                        />
                      </Box>
                      <Box sx={{ flex: '1 1 300px', minWidth: '300px' }}>
                        <Controller
                          name="category"
                          control={control}
                          rules={{ required: 'Category is required' }}
                          render={({ field }) => (
                            <FormControl fullWidth error={!!errors.category}>
                              <InputLabel>Category</InputLabel>
                              <Select {...field} label="Category">
                                {categories.map((category) => (
                                  <MenuItem key={category.id} value={category.name}>
                                    {category.name}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          )}
                        />
                      </Box>
                    </Box>
                    <Controller
                      name="description"
                      control={control}
                      rules={{ required: 'Description is required' }}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Description"
                          multiline
                          rows={3}
                          fullWidth
                          error={!!errors.description}
                          helperText={errors.description?.message}
                        />
                      )}
                    />
                  </Box>
                </AccordionDetails>
              </Accordion>

              {/* Weight-Based Pricing */}
              <Accordion defaultExpanded>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="h6">Quantity-Based Pricing</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 3 }}>
                    <Box sx={{ flex: '1 1 250px', minWidth: '250px' }}>
                      <Controller
                        name="originalPrice"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            label={unit === 'litre' ? 'MRP per Litre' : unit === 'kg' ? 'MRP per Kg' : unit === 'piece' ? 'MRP per Piece' : unit === 'g' ? 'MRP per Gram' : unit === 'ml' ? 'MRP per ML' : 'MRP per Unit'}
                            type="number"
                            fullWidth
                            size="small"
                            InputProps={{
                              startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                            }}
                            helperText="Maximum Retail Price (optional)"
                          />
                        )}
                      />
                    </Box>
                    <Box sx={{ flex: '1 1 250px', minWidth: '250px' }}>
                      <Controller
                        name="pricePerKg"
                        control={control}
                        rules={{ required: 'Selling price is required', min: { value: 0.01, message: 'Price must be greater than 0' } }}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            label={unit === 'litre' ? 'Selling Price per Litre' : unit === 'kg' ? 'Selling Price per Kg' : unit === 'piece' ? 'Selling Price per Piece' : unit === 'g' ? 'Selling Price per Gram' : unit === 'ml' ? 'Selling Price per ML' : 'Selling Price per Unit'}
                            type="number"
                            fullWidth
                            size="small"
                            InputProps={{
                              startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                            }}
                            error={!!errors.pricePerKg}
                            helperText={errors.pricePerKg?.message || "Base price - use Add Option to create weight variants"}
                          />
                        )}
                      />
                    </Box>
                    <Box sx={{ flex: '1 1 200px', minWidth: '200px' }}>
                      <Controller
                        name="unit"
                        control={control}
                        render={({ field }) => (
                          <FormControl fullWidth size="small">
                            <InputLabel>Unit</InputLabel>
                            <Select {...field} label="Unit">
                              <MenuItem value="kg">Kilogram (kg)</MenuItem>
                              <MenuItem value="litre">Litre (L)</MenuItem>
                              <MenuItem value="g">Gram (g)</MenuItem>
                              <MenuItem value="ml">Millilitre (ml)</MenuItem>
                              <MenuItem value="piece">Piece</MenuItem>
                            </Select>
                          </FormControl>
                        )}
                      />
                    </Box>
                  </Box>

                  <Divider sx={{ my: 2 }} />
                  
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      {unit === 'litre' ? 'Volume Options & Stock' : unit === 'kg' ? 'Weight Options & Stock' : unit === 'piece' ? 'Individual Piece Stock' : unit === 'g' ? 'Gram Stock' : unit === 'ml' ? 'Millilitre Stock' : 'Quantity Options & Stock'}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button
                        variant="outlined"
                        size="small"
                        color="secondary"
                        startIcon={<QuickIcon />}
                        onClick={handleCalculatePrices}
                        disabled={!pricePerKg || pricePerKg <= 0}
                      >
                        Calculate Prices
                      </Button>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={() => {
                          const templates = weightTemplates[unit] || [];
                          const currentWeights = watch('weightPrices')?.map((wp: any) => wp.weight) || [];
                          const availableTemplates = templates.filter(t => !currentWeights.includes(t));
                          const nextWeight = availableTemplates.length > 0 ? availableTemplates[0] : templates[0] || '';
                          append({ weight: nextWeight, price: 0, originalPrice: 0, stockQuantity: 0 });
                        }}
                      >
                        Add Option
                      </Button>
                    </Box>
                  </Box>
                  
                  {fields.length === 0 && (
                    <Alert severity="info" sx={{ mb: 2 }}>
                      Weight options will be auto-populated based on selected unit. Click "Calculate Prices" to fill prices automatically.
                    </Alert>
                  )}
                  
                  {fields.length > 0 && (
                    <Alert severity="success" sx={{ mb: 2 }}>
                      💡 <strong>Tip:</strong> Predefined weight options loaded. Click "Calculate Prices" to auto-fill MRP and Selling prices. Delete unwanted rows using the trash icon.
                    </Alert>
                  )}
                  
                  {fields.map((field, index) => (
                    <Box key={field.id} sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'flex-start' }}>
                      <Box sx={{ flex: '0 0 150px' }}>
                        <Controller
                          name={`weightPrices.${index}.weight`}
                          control={control}
                          render={({ field }) => (
                            <FormControl fullWidth size="small">
                              <InputLabel>{unit === 'litre' ? 'Volume' : unit === 'kg' ? 'Weight' : unit === 'piece' ? 'Unit' : unit === 'g' ? 'Weight' : unit === 'ml' ? 'Volume' : 'Quantity'}</InputLabel>
                              <Select
                                {...field}
                                label={unit === 'litre' ? 'Volume' : unit === 'kg' ? 'Weight' : unit === 'piece' ? 'Unit' : unit === 'g' ? 'Weight' : unit === 'ml' ? 'Volume' : 'Quantity'}
                              >
                                {(weightTemplates[unit] || []).map(weight => (
                                  <MenuItem key={weight} value={weight}>{weight}</MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          )}
                        />
                      </Box>
                      <Box sx={{ flex: '1 1 140px' }}>
                        <Controller
                          name={`weightPrices.${index}.originalPrice`}
                          control={control}
                          render={({ field }) => (
                            <TextField
                              {...field}
                              label="MRP"
                              type="number"
                              fullWidth
                              size="small"
                              InputProps={{
                                startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                              }}
                            />
                          )}
                        />
                      </Box>
                      <Box sx={{ flex: '1 1 140px' }}>
                        <Controller
                          name={`weightPrices.${index}.price`}
                          control={control}
                          render={({ field }) => (
                            <TextField
                              {...field}
                              label="Selling Price"
                              type="number"
                              fullWidth
                              size="small"
                              InputProps={{
                                startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                              }}
                            />
                          )}
                        />
                      </Box>
                      <Box sx={{ flex: '1 1 140px' }}>
                        <Controller
                          name={`weightPrices.${index}.stockQuantity`}
                          control={control}
                          render={({ field }) => (
                            <TextField
                              {...field}
                              label="Stock Quantity"
                              type="number"
                              fullWidth
                              size="small"
                            />
                          )}
                        />
                      </Box>
                      <Box sx={{ flex: '0 0 auto' }}>
                        <IconButton
                          color="error"
                          onClick={() => remove(index)}
                          size="small"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    </Box>
                  ))}
                </AccordionDetails>
              </Accordion>

              {/* Images */}
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="h6">Product Images</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Controller
                    name="images"
                    control={control}
                    render={({ field }) => (
                      <ImageUpload
                        images={field.value}
                        onImagesChange={field.onChange}
                        maxImages={5}
                      />
                    )}
                  />
                </AccordionDetails>
              </Accordion>

              {/* Settings */}
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="h6">Product Settings</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                      <Controller
                        name="isAvailable"
                        control={control}
                        render={({ field }) => (
                          <FormControlLabel
                            control={<Switch {...field} checked={field.value} />}
                            label="Available for Sale"
                          />
                        )}
                      />
                      <Controller
                        name="isOrganic"
                        control={control}
                        render={({ field }) => (
                          <FormControlLabel
                            control={<Switch {...field} checked={field.value} />}
                            label="Organic Product"
                          />
                        )}
                      />
                      <Controller
                        name="preOrderOnly"
                        control={control}
                        render={({ field }) => (
                          <FormControlLabel
                            control={<Switch {...field} checked={field.value} />}
                            label="Pre-order Only"
                          />
                        )}
                      />
                      <Controller
                        name="isSeasonal"
                        control={control}
                        render={({ field }) => (
                          <FormControlLabel
                            control={<Switch {...field} checked={field.value} />}
                            label="Seasonal Product"
                          />
                        )}
                      />
                    </Box>
                    <Box sx={{ maxWidth: 300 }}>
                      <Controller
                        name="minOrderQuantity"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            label="Minimum Order Quantity"
                            type="number"
                            fullWidth
                          />
                        )}
                      />
                    </Box>
                  </Box>
                </AccordionDetails>
              </Accordion>

              {/* Product Details */}
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="h6">Product Details</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    <Controller
                      name="healthBenefits"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Health Benefits"
                          multiline
                          rows={3}
                          fullWidth
                          placeholder="e.g., Contains Folic acid, Vitamin C and Amino acid. Vitamin C acts as a powerful antioxidant..."
                        />
                      )}
                    />
                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                      <Box sx={{ flex: '1 1 200px' }}>
                        <Controller
                          name="storageTemperature"
                          control={control}
                          render={({ field }) => (
                            <TextField
                              {...field}
                              label="Storage Temperature (°C)"
                              fullWidth
                              placeholder="e.g., 7-13"
                            />
                          )}
                        />
                      </Box>
                      <Box sx={{ flex: '1 1 200px' }}>
                        <Controller
                          name="shelfLife"
                          control={control}
                          render={({ field }) => (
                            <TextField
                              {...field}
                              label="Shelf Life"
                              fullWidth
                              placeholder="e.g., 4 days"
                            />
                          )}
                        />
                      </Box>
                      <Box sx={{ flex: '1 1 200px' }}>
                        <Controller
                          name="countryOfOrigin"
                          control={control}
                          render={({ field }) => (
                            <TextField
                              {...field}
                              label="Country of Origin"
                              fullWidth
                              placeholder="India"
                            />
                          )}
                        />
                      </Box>
                    </Box>
                    <Controller
                      name="storageTips"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Storage Tips"
                          multiline
                          rows={2}
                          fullWidth
                          placeholder="e.g., Store in wire-basket or mesh bags."
                        />
                      )}
                    />
                  </Box>
                </AccordionDetails>
              </Accordion>

              {/* Legal & Compliance */}
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="h6">Legal & Compliance</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    <Controller
                      name="disclaimer"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Disclaimer"
                          multiline
                          rows={2}
                          fullWidth
                          placeholder="e.g., Product image is for representation only and actual product might differ..."
                        />
                      )}
                    />
                    <Controller
                      name="returnPolicy"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Return Policy"
                          multiline
                          rows={3}
                          fullWidth
                          placeholder="e.g., The product is non-returnable. For a damaged, rotten or incorrect item..."
                        />
                      )}
                    />
                    <Controller
                      name="customerCareEmail"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Customer Care Email"
                          type="email"
                          fullWidth
                          placeholder="e.g., info@shetkari.com"
                        />
                      )}
                    />
                  </Box>
                </AccordionDetails>
              </Accordion>
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 3 }}>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            <Button type="submit" variant="contained" disabled={isSubmitting}>
              {editingProduct ? 'Update' : 'Create'} Product
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 600 }}>
          Delete Product?
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            Are you sure you want to delete "{productToDelete?.name}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button 
            onClick={() => setDeleteDialogOpen(false)}
            sx={{ textTransform: 'none' }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Quick Price Update Dialog */}
      <QuickPriceUpdate
        open={quickPriceOpen}
        onClose={() => setQuickPriceOpen(false)}
        product={selectedProduct}
        onUpdate={handlePriceUpdate}
      />

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ProductsWeightBased;
